import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, History, X } from 'lucide-react';
import { listReminderDispatches } from '../../../../api/collections';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { Button } from '../../../../components/ui/Button';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { Select } from '../../../../components/ui/Select';
import { TableSkeleton } from '../../../../components/ui/TableSkeleton';
import DispatchStatusBadge from '../components/DispatchStatusBadge';
import ReminderMessageModal from '../components/ReminderMessageModal';
import { DispatchDetail, useDispatchDates } from '../components/dispatchParts';
import {
  CHANNEL_LABELS,
  DISPATCH_STATUSES,
  DISPATCH_STATUS_LABELS,
  REMINDER_CHANNELS,
  TRIGGER_LABELS,
  collectionsErrorText,
} from '../utils/collectionReminders';

const PAGE_SIZE = 50;
// Fecha · Nota · Comercio · Canal · Origen · Estado · Detalle
const GRID_COLS = '1fr 0.6fr 1.3fr 1.2fr 0.8fr 0.9fr 1.6fr';

/**
 * Historial de recordatorios del operador (todas las notas), con filtros por estado, canal y
 * origen. Un envío programado se puede ver aislado (`batchId`, desde "Programados"); el
 * padre lo monta con `key={batchId}` para que cambiarlo reinicie filtros y página.
 *
 * @param {{ timeZone?: string, batchId?: string, onClearBatch: () => void }} props
 */
export default function ReminderDispatchesTab({ timeZone, batchId, onClearBatch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const datesOf = useDispatchDates(timeZone);
  const [filters, setFilters] = useState({ status: '', channel: '', trigger: '' });
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fila cuyo mensaje se está viendo ("Ver mensaje"), o null.
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { limit: PAGE_SIZE, offset };
    if (filters.status) params.status = filters.status;
    if (filters.channel) params.channel = filters.channel;
    if (filters.trigger) params.trigger = filters.trigger;
    if (batchId) params.batchId = batchId;
    try {
      const { data } = await listReminderDispatches(params);
      setPage({ items: data?.items ?? [], count: data?.count ?? 0 });
    } catch (err) {
      setError(collectionsErrorText(err, t, 'No se pudo cargar el historial de recordatorios.'));
    } finally {
      setLoading(false);
    }
  }, [filters, offset, batchId, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Cambiar un filtro vuelve a la primera página.
  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setOffset(0);
  };
  const filtered = !!(filters.status || filters.channel || filters.trigger || batchId);
  const totalPages = Math.max(1, Math.ceil(page.count / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const noteCell = (d) =>
    d.note ? (
      <button
        type="button"
        className="bill-td-track text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        onClick={() => navigate(`/admin/cobro/${d.noteId}`)}
        title={t('Ver la nota')}
      >
        #{d.note.folio ?? '—'}
      </button>
    ) : (
      <span className="bill-td-sub" title={t('La nota ya no existe.')}>—</span>
    );

  const channelCell = (d) => (
    <span className="block">
      {t(CHANNEL_LABELS[d.channel] ?? d.channel)}
      {d.recipientsMasked?.length > 0 && (
        <span className="bill-td-sub block break-all">{d.recipientsMasked.join(', ')}</span>
      )}
    </span>
  );

  return (
    <div className="space-y-3">
      <div className="bill-card" style={{ overflow: 'visible' }}>
        <div className="bill-filters">
          <Select
            label={t('Estado')}
            value={filters.status}
            onChange={(e) => setFilter({ status: e.target.value })}
          >
            <option value="">{t('Todos los estados')}</option>
            {DISPATCH_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(DISPATCH_STATUS_LABELS[s])}
              </option>
            ))}
          </Select>
          <Select
            label={t('Canal')}
            value={filters.channel}
            onChange={(e) => setFilter({ channel: e.target.value })}
          >
            <option value="">{t('Todos los canales')}</option>
            {REMINDER_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(CHANNEL_LABELS[c])}
              </option>
            ))}
          </Select>
          <Select
            label={t('Origen')}
            value={filters.trigger}
            onChange={(e) => setFilter({ trigger: e.target.value })}
          >
            <option value="">{t('Todos los orígenes')}</option>
            {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {batchId && (
        <div className="flex">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-500 bg-brand-50/60 py-1 pl-3 pr-1 text-xs font-medium text-brand-700">
            {t('Solo un envío programado')}
            <button
              type="button"
              onClick={onClearBatch}
              aria-label={t('Ver todos los envíos')}
              title={t('Ver todos los envíos')}
              className="inline-flex items-center justify-center rounded-full p-1 hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </span>
        </div>
      )}

      {timeZone && <p className="text-xs text-fg-muted">{t('Horas en {{zona}}, la zona del operador.', { zona: timeZone })}</p>}

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            {t('Reintentar')}
          </Button>
        </div>
      )}

      <div className="bill-card bill-card--clip">
        {loading ? (
          <TableSkeleton rows={8} columns={7} className="border-0" />
        ) : page.items.length === 0 ? (
          !error && (
            <EmptyState
              icon={<History />}
              title={filtered ? t('Sin resultados para estos filtros') : t('Todavía no se enviaron recordatorios')}
              description={
                filtered
                  ? t('Probá con otro estado, canal u origen.')
                  : t('Acá vas a ver cada recordatorio: automático, programado o manual, con su resultado.')
              }
            />
          )
        ) : isMobile ? (
          <div className="bill-cards">
            {page.items.map((d) => {
              const dates = datesOf(d);
              return (
                <div key={d._id} className="bill-card-item">
                  <div className="bill-card-item__head">
                    <span className="bill-card-item__title">{d.note?.companyName || t('Sin nombre')}</span>
                    <DispatchStatusBadge status={d.status} size="sm" />
                  </div>
                  <div className="bill-card-item__rows">
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Nota')}</span>
                      <span className="bill-card-item__v">{noteCell(d)}</span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Fecha')}</span>
                      <span className="bill-card-item__v">
                        {dates.main}
                        {dates.sub && <span className="bill-td-sub block">{dates.sub}</span>}
                      </span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Canal')}</span>
                      <span className="bill-card-item__v">{channelCell(d)}</span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Origen')}</span>
                      <span className="bill-card-item__v">{t(TRIGGER_LABELS[d.trigger] ?? d.trigger)}</span>
                    </div>
                    <div className="bill-card-item__field bill-card-item__field--full">
                      <span className="bill-card-item__k">{t('Detalle')}</span>
                      <span className="bill-card-item__v">
                        <DispatchDetail dispatch={d} onViewMessage={setViewing} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Fecha')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Nota')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Comercio')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Canal')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Origen')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Detalle')}</span>
            </div>
            {page.items.map((d) => {
              const dates = datesOf(d);
              return (
                <div key={d._id} className="bill-grid__row" role="row">
                  <span className="bill-td-muted" role="cell">
                    {dates.main}
                    {dates.sub && <span className="bill-td-sub block">{dates.sub}</span>}
                  </span>
                  <span role="cell">{noteCell(d)}</span>
                  <span className="bill-td-strong" role="cell">
                    {d.note?.companyName || <span className="bill-td-sub">—</span>}
                  </span>
                  <span className="bill-td-muted" role="cell">{channelCell(d)}</span>
                  <span className="bill-td-muted" role="cell">{t(TRIGGER_LABELS[d.trigger] ?? d.trigger)}</span>
                  <span role="cell">
                    <DispatchStatusBadge status={d.status} size="sm" />
                  </span>
                  <span className="bill-td-muted" role="cell">
                    <DispatchDetail dispatch={d} onViewMessage={setViewing} />
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label={t('Anterior')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              {t('Página')} {currentPage} {t('de')} {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label={t('Siguiente')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {viewing && (
        <ReminderMessageModal dispatch={viewing} onClose={() => setViewing(null)} timeZone={timeZone} />
      )}
    </div>
  );
}
